import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  updateMultipleTemplates,
  updateTemplateAnimation,
} from "../../api/faceswap/categoryTemplate";
import { useMedia } from "../../context/media-context";
import MediaActions from "./MediaActions";
import MediaDetailsSkelton from "./MediaDetailsSkelton";
import MediaHeader from "./MediaHeader";
import MediaTags from "./MediaTags";
import MediaThumbnail from "./MediaThumbnail";
import UploadGifSection from "./UploadGifSection";
import DeleteSpecificMedia from "../common/DeleteSpecificMedia";

const MediaDetails = ({ mediaType }) => {
  const { allValues, setAllValues, handleDeleteMedia } = useOutletContext();
  const queryClient = useQueryClient();
  const { mediaId } = useParams();
  const [currentMedia, setCurrentMedia] = useState();

  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 5MB
  const [errorMessage, setErrorMessage] = useState({
    animated_previewError: "",
  });
  const [animated_preview, setAnimated_preview] = useState(null); //state to hold gif or video

  const animated_previewRef = useRef(null);
  const { media } = useMedia();
  const [selectedValue, setSelectedValue] = useState({
    id: "",
    isfeatured: false,
    istrending: false,
    ismultifaced: false,
    ispremium: false,
    premiumfor: "ALL",
  });

  // Use ref to store the initial values so we can reset to them
  const initialSelectedValue = useRef(selectedValue);

  const [isLoading, setIsLoading] = useState(false);

  const handleImageLoad = () => {
    setIsLoading(false);
  };
  useEffect(() => {
    setIsLoading(true);
  }, [mediaId]);

  useEffect(() => {
    if (!media || !mediaId) return;

    const IsInAllValues = allValues.find((item) => item.id === Number(mediaId));
    const item = media.templates?.find((item) => item.id === Number(mediaId));

    if (item) {
      setCurrentMedia(item);
    }

    if (IsInAllValues) {
      const newSelectedValue = {
        id: IsInAllValues.id,
        isfeatured: IsInAllValues.isfeatured,
        istrending: IsInAllValues.istrending,
        ismultifaced: IsInAllValues.ismultifaced,
        ispremium: IsInAllValues.ispremium,
        premiumfor: IsInAllValues.premiumfor || "ALL",
      };
      setSelectedValue(newSelectedValue);
      initialSelectedValue.current = newSelectedValue;
    } else if (item) {
      const newSelectedValue = {
        id: item.id,
        isfeatured: item.is_featured,
        istrending: item.is_trending,
        ismultifaced: item.is_multi_faced,
        ispremium: item.is_premium,
        premiumfor: item.premium_for || "ALL",
      };
      setSelectedValue(newSelectedValue);
      initialSelectedValue.current = newSelectedValue;
    }
  }, [media, mediaId, allValues]);

  const updateTemplates = useMutation({
    mutationFn: (payload) => updateMultipleTemplates(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["getSpecificCategoryTemplate", mediaId]);
      toast.success("Template updated successfully!");
    },
    onError: (error) => {
      toast.error(`Error updating template: ${error.message}`);
    },
  });

  const updateTemplateAnimationMutation = useMutation({
    mutationFn: ({ templateId, animationFile }) =>
      updateTemplateAnimation(templateId, animationFile),
    onSuccess: () => {
      queryClient.invalidateQueries(["getSpecificCategoryTemplate", mediaId]);
      toast.success("GIF updated successfully!");
    },
    onError: (error) => {
      console.error("Error in mutation:", error.message);
      toast.error("Failed to update GIF. Please try again.");
    },
  });

  // Handle input changes
  const handleInputChange = (event) => {
    const { name, type, checked, value } = event.target;

    console.log(name, type, checked, value);

    // Update the selected value for the form's current state
    setSelectedValue((prevValue) => {
      const updatedValue = {
        ...prevValue,
        [name]: type === "checkbox" ? checked : value,
      };

      setAllValues((prev) => {
        const index = prev.findIndex((item) => item.id === updatedValue.id);

        if (index !== -1) {
          // Update the existing item if id matches
          const updatedAllValues = [...prev];
          updatedAllValues[index] = {
            ...updatedAllValues[index],
            ...updatedValue,
          };
          return updatedAllValues;
        } else {
          // Add new item if id does not exist
          return [...prev, updatedValue];
        }
      });

      return updatedValue;
    });
  };

  // Handle save action
  const handleSave = () => {
    const payload = [selectedValue];

    updateTemplates.mutate(payload);
  };

  // Handle reset action
  const handleReset = () => {
    setSelectedValue(initialSelectedValue.current);

    setAllValues((prev) => prev.filter((item) => item.id !== selectedValue.id));
  };

  if (!media) {
    return <MediaDetailsSkelton />;
  }

  if (!currentMedia) {
    return <MediaDetailsSkelton />;
  }

  const handleanimated_previewUpload = (event) => {
    const file = event.target.files[0]; // Single file upload for GIF or video
    if (file) {
      const fileType = file.type.toLowerCase();
      const isValidType = fileType === "image/gif";
      const isSizeValid = file.size <= MAX_FILE_SIZE;

      if (!isValidType) {
        setErrorMessage((prevErrors) => ({
          ...prevErrors,
          animated_previewError: `${file.name} is an invalid file type.`,
        }));
        return;
      }
      if (!isSizeValid) {
        setErrorMessage((prevErrors) => ({
          ...prevErrors,
          animated_previewError: `${file.name} is too large.`,
        }));
        return;
      }

      setErrorMessage((prevErrors) => ({
        ...prevErrors,
        animated_previewError: "",
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        const preview = reader.result;

        // Call the mutation
        updateTemplateAnimationMutation.mutate({
          templateId: currentMedia.id,
          animationFile: file,
        });

        setAnimated_preview({ file, preview });
      };
      reader.readAsDataURL(file);
    }
  };
  const fileUrl =
    mediaType === "videos" ? currentMedia?.file : currentMedia?.thumbnail;

  const mediaThumbnailUrl = `https://faceswap.cognise.art/${fileUrl}`;
  return (
    <article className="relative mb-[40px] mt-[29px] grid grid-cols-1 gap-[30px] px-[30px] lg:grid-cols-2 xl:grid-cols-3">
      {/* Media Thumbnail Section */}
      <section className="group relative xl:order-1">
        <MediaThumbnail
          isLoading={isLoading}
          src={mediaThumbnailUrl}
          alt={`${mediaType} ${currentMedia.id}`}
          onLoad={handleImageLoad}
          type={mediaType}
        />
        {!isLoading && (
          <DeleteSpecificMedia
            className="left-3 top-3 z-10 rounded-md bg-red-200 p-1"
            onClick={() => {
              handleDeleteMedia(currentMedia?.id);
              handleReset();
            }}
          />
        )}
      </section>

      {/* Media Details Section */}
      <section className="order-3 w-full max-w-[350px] xl:order-2">
        <MediaHeader title={currentMedia.title} />
        <MediaTags tags={currentMedia.tags} />
        <MediaActions
          allValues={allValues}
          handleReset={handleReset}
          handleSave={handleSave}
          selectedValue={selectedValue}
          handleInputChange={handleInputChange}
        />
      </section>
      {/* Media Thumbnail Section */}
      <section className="group relative order-2 h-[400px] w-full max-w-[350px] place-content-center xl:order-3">
        <UploadGifSection
          animatedPreviewRef={animated_previewRef}
          onUpload={handleanimated_previewUpload}
          imageLoad={handleImageLoad}
          animated_preview={currentMedia?.animated_preview}
          isUploading={updateTemplateAnimationMutation.isPending}
          errorMessage={errorMessage.animated_previewError}
        />
      </section>
    </article>
  );
};

export default MediaDetails;
